/**
 * Тексты административных экранов: справочники (§9.1, §9.2), поля (§9.3),
 * пользователи и роли (§9.4), настройки (§9.5) и отказы по правам (§11.4).
 *
 * Общий `web/src/texts.ts` правят другие волны, поэтому свои строки держим
 * здесь, а склонение числительных берём оттуда — словарь форм один на систему.
 */
import { countOf, forms, plural } from '../../texts.js'

/** Формы, которые нужны только административным экранам. */
export const adminForms = {
  binding: ['привязка', 'привязки', 'привязок'] as const,
  value: ['значение', 'значения', 'значений'] as const,
  record: ['записи', 'записях', 'записях'] as const,
  user: ['пользователь', 'пользователя', 'пользователей'] as const,
  field: ['поле', 'поля', 'полей'] as const,
  attempt: ['попытка', 'попытки', 'попыток'] as const,
}

/** «53 бренда» / «1 бренд» — в предупреждениях спеки число всегда названо. */
const brandsOf = (n: number) => countOf(n, forms.brand)

export const adminTexts = {
  /** Общее для всех четырёх экранов (§9). */
  common: {
    loading: 'Загружаем справочник',
    retry: 'Повторить',
    cancel: 'Отмена',
    save: 'Сохранить',
    saving: 'Сохраняем…',
    rename: 'Переименовать',
    renameApply: 'Готово',
    close: 'Закрыть',
    copy: 'Скопировать',
    copied: 'Скопировано',
    rowMenu: 'Действия со строкой',
    moveUp: 'Выше',
    moveDown: 'Ниже',
    statusActive: 'Активна',
    statusArchived: 'В архиве',
    immediate: 'Правки справочников применяются сразу — без черновика, но с записью в журнал и строкой в Telegram.',
    loadFailed: 'Не удалось загрузить данные раздела',
    required: 'Поле обязательно к заполнению',
  },

  /** §9.1 Справочник категорий */
  categories: {
    title: 'Справочник категорий',
    add: 'Категория',
    count: (n: number) => `${countOf(n, forms.category)} в справочнике`,
    colName: 'Название',
    colBrands: 'Брендов',
    colStatus: 'Статус',
    colActions: 'Действия',
    nameLabel: 'Название категории',
    namePlaceholder: 'Например: Холодильники',
    createTitle: 'Новая категория',
    createSubmit: 'Добавить',
    createConsequence: 'Категорию сразу можно будет выбрать у бренда. Название видно всем.',
    renameConsequence: 'Название изменится у всех брендов, в истории и в уведомлениях. Привязки не ломаются.',
    renameHint: 'Enter — применить, Esc — отменить',
    menuRename: 'Переименовать',
    menuArchive: 'Архивировать',
    menuMerge: 'Объединить с другой…',
    menuDelete: 'Удалить',
    deleteReasonUsed: (n: number) =>
      `У категории ${countOf(n, adminForms.binding)}. Её можно архивировать или объединить с другой`,
    deleteReasonUnsupported: 'Удаление категорий из интерфейса пока не предусмотрено — архивируйте её',
    archiveReasonArchived: 'Категория уже в архиве',
    mergeReasonArchived: 'Категория в архиве — сливать её не нужно',
    archiveTitle: 'Архивировать категорию',
    archiveQuestion: (name: string) => `Категория «${name}»`,
    archiveUsed: (n: number) =>
      `У категории ${countOf(n, adminForms.binding)}. После архивации она останется у этих брендов, ` +
      'но выбрать её для новых будет нельзя.',
    archiveFree: 'Категория ни у одного бренда не стоит. После архивации выбрать её для новых будет нельзя.',
    archiveSubmit: 'Архивировать',
    archiveNote: 'Из архива категорию возвращает разработчик — решайте осознанно',
    toastCreated: (name: string) => `Категория «${name}» добавлена`,
    toastRenamed: (name: string) => `Категория переименована в «${name}»`,
    toastArchived: (name: string) => `Категория «${name}» в архиве`,
    emptyTitle: 'В справочнике нет категорий',
    emptyText: 'Добавьте первую — её сразу можно будет выбрать у бренда',
  },

  /** §9.1 Слияние дублей */
  merge: {
    title: 'Объединить категории',
    from: 'Из',
    to: 'В',
    targetLabel: 'Категория, в которую сливаем',
    targetEmpty: '— выберите категорию',
    sourceSummary: (name: string, n: number) => `«${name}» · ${brandsOf(n)}`,
    explain: (source: string, target: string, n: number) =>
      `После объединения ${countOf(n, adminForms.binding)} перейдут в «${target}». Категория «${source}» ` +
      'будет архивирована. Приоритеты по каналам сохранятся; если у бренда окажутся обе категории, ' +
      `останется приоритет из «${target}».`,
    explainNoTarget: 'Выберите категорию, в которую переедут привязки.',
    note: 'Объединение необратимо',
    submit: 'Объединить',
    submitting: 'Объединяем…',
    toastDone: (source: string, target: string) => `«${source}» объединена с «${target}»`,
  },

  /** §9.2 Справочники значений */
  enums: {
    title: 'Справочники значений',
    setsLabel: 'Наборы значений',
    addSet: '+ Новый справочник',
    createSetTitle: 'Новый справочник',
    createSetIntro: 'Справочник — это набор значений для одной колонки: «Приоритет SEO», «Видимость на сайте». Сначала заводим сам справочник, потом его значения.',
    setName: 'Название',
    setNamePlaceholder: 'Например: Тип подключения',
    setNameHint: 'Видно в списке слева и в настройке колонки',
    setCode: 'Код справочника',
    setCodePlaceholder: 'например connection_type',
    setCodeHint: 'Латиница в нижнем регистре, цифры и подчёркивание. Изменить потом нельзя.',
    createSetSubmit: 'Создать справочник',
    toastSetCreated: (name: string) => `Справочник «${name}» создан`,
    orderNote: 'Порядок значений задаёт сортировку «по приоритету» в таблице и порядок в поповерах правки.',
    lockedNote: 'Набор защищён: значения используются в логике сортировки',
    valueImmutable: 'Подпись меняется для людей. Хранимое значение остаётся в данных и в истории — его не изменить.',
    colLabel: 'Подпись',
    colValue: 'Значение / код',
    colColor: 'Цвет',
    colHint: 'Пояснение',
    hintAdd: '+ пояснение',
    hintPlaceholder: 'Что значит этот тег',
    toastHint: (label: string) => `Пояснение к «${label}» сохранено`,
    colUsage: 'Используется у',
    colActive: 'Активно',
    colOrder: 'Порядок',
    colorNote: 'Цвет чипа виден и в таблице, и в выпадающем списке. Значение без цвета рисуется серым.',
    colorPick: (label: string) => `Цвет значения «${label}»`,
    toastColor: (label: string, color: string) => `«${label}» — ${color.toLowerCase()}`,
    usageOf: (n: number) => `${n} ${plural(n, adminForms.record)}`,
    usageZero: 'нигде',
    add: 'Добавить ещё одно значение',
    createTitle: (set: string) => `Новое значение · ${set}`,
    createSubmit: 'Добавить',
    createIntro: 'У значения две части. Подпись видят люди — её можно менять когда угодно. Код уходит в данные и в историю, он постоянный: по нему значение узнаётся в выгрузках и в журнале.',
    labelField: 'Подпись для людей',
    labelPlaceholder: 'Например: TOP-1',
    labelHint: 'Видно в таблице, в выпадающих списках и в уведомлениях',
    valueField: 'Хранимое значение (код)',
    valuePlaceholder: 'например top1',
    valueHint: 'Латиница, цифры и знаки _ + - . Людям его не показывают; изменить потом нельзя.',
    activeOn: (label: string) => `«${label}» предлагается в правках`,
    activeOff: (label: string) =>
      `«${label}» останется в данных и в истории, но в новых правках предлагаться не будет`,
    deleteNote: 'Значения не удаляются: отключите значение — данные и история останутся целыми.',
    toastCreated: (label: string) => `Значение «${label}» добавлено`,
    toastRenamed: (label: string) => `Подпись изменена на «${label}»`,
    toastActive: (label: string, active: boolean) =>
      active ? `«${label}» снова предлагается в правках` : `«${label}» отключено`,
    toastOrder: 'Порядок значений изменён',
    emptyTitle: 'В наборе нет значений',
    emptyText: 'Добавьте первое — оно появится в поповерах правки',
  },

  /** §9.3 Поля */
  fields: {
    colName: 'Название',
    colCode: 'Код',
    colType: 'Тип',
    colScope: 'Область',
    colRequired: 'Обязательное',
    colShow: 'В таблице по умолчанию',
    colOrder: 'Порядок',
    colStatus: 'Статус',
    viewOnly: 'Раздел открыт на просмотр. Менять порядок и показ в таблице может тот, у кого есть право «Конструктор полей» — его выдаёт собственник.',
    schemaNote: 'Доставка, установка и гарантия — обычные колонки схемы, а не пользовательские поля: в этом списке их нет.',
    emptyTitle: 'Пользовательских полей пока нет',
    emptyText: 'Все колонки таблицы сейчас встроенные. Конструктор полей появится на этапе 2.',
    yes: 'Да',
    no: 'Нет',
    showOn: (name: string) => `«${name}» показывается в таблице сразу`,
    showOff: (name: string) => `«${name}» останется доступным через «Колонки», но по умолчанию скрыт`,
    toastSaved: (name: string) => `Поле «${name}» обновлено`,
    toastOrder: 'Порядок полей изменён',
    statusActive: 'Активно',
    statusArchived: 'В архиве',
    type: {
      text: 'Строка', textarea: 'Текст', number: 'Число', date: 'Дата', bool: 'Да / нет',
      url: 'Ссылка', select: 'Выбор одного', multiselect: 'Выбор нескольких', user: 'Пользователь',
    },
    scope: {
      brand: 'Бренд', brand_channel: 'Бренд × канал', brand_category: 'Категория бренда',
    },
  },

  /** §9.4 Пользователи и роли */
  users: {
    tabs: { label: 'Разделы администрирования', users: 'Пользователи', roles: 'Роли' },
    add: 'Пользователь',
    count: (total: number, active: number) => `${countOf(total, adminForms.user)} · активных ${active}`,
    mustChange: 'пароль не сменён',
    colName: 'ФИО',
    colShortName: 'Короткое имя',
    colColor: 'Цвет',
    colorAuto: 'Цвет по имени',
    colorPick: 'Назначить цвет',
    colEmail: 'E-mail',
    colLoginCode: 'Код входа',
    menuGiveCode: 'Выдать код входа',
    menuNewCode: 'Сменить код входа',
    menuRevokeCode: 'Отозвать код входа',
    codeShow: 'Показать код',
    serviceShow: (n: number) => `Показать служебные учётки (${n})`,
    serviceHide: 'Скрыть служебные учётки',
    codeCopyLink: 'Скопировать ссылку с кодом',
    copyAll: 'Скопировать все ссылки',
    copyAllDone: (n: number) => `Скопировано ссылок: ${n}. Осталось разослать по личкам.`,
    copyAllNone: 'Ни у кого нет кода входа — сначала выдайте их в меню строки',
    copyAllMissing: (names: string) => `Без кода входа, в список не попали: ${names}`,
    codeCopied: (name: string) => `Ссылка с кодом для ${name} скопирована`,
    codeRevoked: (name: string) => `Код для ${name} отозван, сессии закрыты`,
    colRole: 'Роль',
    colJob: 'Должность',
    colArea: 'Зона ответственности',
    colActive: 'Активен',
    colLastLogin: 'Последний вход',
    neverLoggedIn: 'ни разу',
    roleSelect: (name: string) => `Роль пользователя ${name}`,
    activeToggle: (name: string) => `Пользователь ${name} активен`,
    areaFromRole: 'Зона ответственности приходит из роли и задаёт колонку «Пропущено» (§4.9).',
    /** §4.9: роли.area ∈ commerce | seo | marketing | site | admin | null. */
    area: {
      commerce: 'Коммерция',
      seo: 'SEO',
      marketing: 'Маркетинг',
      site: 'Сайт',
      admin: 'Все поля',
      none: 'не задана',
    } as Record<string, string>,
    createTitle: 'Новый пользователь',
    createSubmit: 'Создать',
    fullName: 'ФИО',
    fullNamePlaceholder: 'Иван Петров',
    shortName: 'Короткое имя',
    shortNameHint: 'Так система подписывает правки: «Гоша Терновский»',
    email: 'E-mail',
    emailHint: 'С него человек входит в систему',
    jobTitle: 'Должность',
    role: 'Роль',
    roleHint: 'От роли зависят права и зона ответственности',
    passwordTitle: 'Одноразовый пароль',
    passwordLead: (name: string) => `Пользователь ${name} создан.`,
    passwordLeadReset: (name: string) => `Пароль пользователя ${name} сброшен.`,
    passwordWarning: 'Передайте пароль лично. Он будет изменён при первом входе.',
    passwordOnce: 'Больше пароль не отобразится — повторно его можно только сбросить.',
    passwordLabel: 'Пароль',
    passwordCopied: 'Пароль скопирован',
    passwordCopyFailed: 'Не удалось скопировать — выделите пароль и скопируйте вручную',
    menuReset: 'Сбросить пароль',
    menuDeactivate: 'Отключить доступ',
    menuActivate: 'Включить доступ',
    selfDeactivateReason: 'Нельзя отключить собственную учётную запись — попросите это сделать другого администратора',
    selfRoleReason: 'Снять с себя права администратора нельзя — пусть роль сменит другой администратор',
    resetTitle: 'Сбросить пароль',
    resetQuestion: (name: string) => `Пользователь ${name}`,
    resetExplain: 'Старый пароль перестанет работать сразу, открытые сессии закроются. Новый пароль покажем один раз.',
    resetSubmit: 'Сбросить',
    toastCreated: (name: string) => `Пользователь ${name} создан`,
    toastRole: (name: string, role: string) => `${name} — теперь «${role}»`,
    toastActive: (name: string, active: boolean) =>
      active ? `Доступ пользователю ${name} включён` : `Доступ пользователю ${name} отключён`,
    rolesMatrixLabel: 'Права ролей',
    rolesColPermission: 'Право',
    rolesSeparationOff: 'Разграничение прав выключено: на этапе 1 все, кроме собственника, работают с полными правами на данные. Матрица ниже показывает, что роль получит, когда разграничение включат.',
    rolesReadOnly: 'Матрица открыта на чтение: менять набор прав роли из интерфейса пока нельзя.',
    rolesSystem: 'Системная роль: набор прав фиксирован',
    rolesUsersCount: (n: number) => `${countOf(n, adminForms.user)}`,
    rolesHas: (role: string, permission: string) => `${role}: ${permission} — есть`,
    rolesHasNot: (role: string, permission: string) => `${role}: ${permission} — нет`,
    emptyTitle: 'Пользователей нет',
    emptyText: 'Добавьте первого — пароль покажем один раз',
  },

  /** §9.5 Настройки */
  settings: {
    groupTelegram: 'Telegram',
    groupCommon: 'Общие',
    groupExport: 'Экспорт',
    telegramLead: 'Одно сообщение на пакет изменений. Личных подписок на этапе 1 нет — чат один.',
    enabled: 'Уведомления в Telegram',
    enabledHint: 'Выключено — пакеты подтверждаются молча, в чат ничего не уходит',
    token: 'Токен бота',
    tokenCurrent: (mask: string) => `Сейчас: ${mask}`,
    tokenEmpty: 'Токен ещё не задан',
    tokenPlaceholder: '123456789:AA…',
    tokenReplace: 'Старый токен будет заменён. Показать его снова система не сможет.',
    tokenKeep: 'Оставьте поле пустым, чтобы сохранить текущий токен',
    chatId: 'ID чата',
    chatIdHint: 'Число, например -1001234567890',
    threadId: 'ID темы (необязательно)',
    threadIdHint: 'Номер темы в чате с форумом',
    test: 'Отправить тестовое сообщение',
    testing: 'Отправляем…',
    testOk: 'Тестовое сообщение отправлено',
    testFail: (message: string) => `Не получилось: ${message}`,
    testNeedsConfig: 'Сначала заполните токен бота и ID чата',
    quietTitle: 'Режим тишины',
    quietEnabled: 'С вечера до утра накапливать и отправлять одним сообщением',
    quietFrom: 'С',
    quietTo: 'До',
    quietHint: 'Время в формате ЧЧ:ММ',
    quietOff: 'Выключен: сообщения уходят сразу после подтверждения пакета',
    eventsTitle: 'О чём писать в чат',
    events: {
      brand_fields: 'Правки полей брендов',
      brand_create: 'Новые бренды',
      categories: 'Категории у брендов',
      comments: 'Комментарии',
      catalog: 'Справочники',
      fields: 'Поля',
      users: 'Пользователи',
      revert: 'Откаты пакетов',
    },
    remindAlways: 'О неподтверждённых правках старше 3 дней напоминаем всегда',
    remind: 'Напоминать о неподтверждённых правках старше 3 дней',
    remindFixed: 'Напоминание работает всегда: отдельной настройки для него на сервере пока нет',
    queueTitle: 'Очередь уведомлений',
    queueChat: (title: string) => `Чат: ${title}`,
    queueNotConfigured: 'Токен или ID чата не заданы — отправлять некуда',
    queueOff: 'Уведомления выключены: очередь не разбирается',
    queueOk: 'Очередь пуста — всё отправлено',
    queuePending: (n: number) => `В очереди ${countOf(n, adminForms.attempt)}`,
    queueFailed: (n: number) => `Не доставлено: ${countOf(n, adminForms.attempt)}`,
    queueLastError: (message: string) => `Последняя ошибка: ${message}`,
    queueAttempts: 'Последние пакеты',
    queueRetry: 'Отправить повторно',
    queueRetryDone: 'Отправили повторно',
    queueNoHistory: 'Список пакетов доступен тем, у кого есть право смотреть историю',
    tgStatus: {
      sent: 'отправлено', queued: 'в очереди', pending: 'в очереди',
      failed: 'ошибка', skipped: 'не отправлялось',
    },
    publicUrl: 'Публичный адрес',
    publicUrlHint: 'Он подставляется в ссылки из уведомлений',
    defaultChannel: 'Канал по умолчанию',
    defaultChannelHint: 'С этим каналом открывается таблица при первом входе',
    defaultSort: 'Сортировка по умолчанию',
    sortAlphabet: 'По алфавиту',
    sortTier: 'По приоритету (тир)',
    pollInterval: 'Интервал опроса, с',
    pollIntervalHint: 'Как часто проверяем, не опубликовал ли кто-то пакет. От 5 до 300 секунд',
    saveGroup: 'Сохранить',
    savedToast: 'Настройки сохранены',
    nothingToSave: 'Ничего не изменилось',
    exportSliceXlsx: '⬇ Выгрузить текущий срез в XLSX',
    exportSlice: '⬇ Выгрузить текущий срез в CSV',
    exportLog: '⬇ Выгрузить журнал в CSV',
    exportLogReason: 'Выгрузка журнала появится вместе с её поддержкой на сервере',
    exportXlsx: 'Выгрузить в XLSX',
    exportSheets: 'Выгрузить в Google-таблицу',
    stage2: 'Этап 2',
  },

  /** §11.4 Нет прав — объяснение внутри доступного раздела */
  denied: {
    fieldsManage: 'Нет права менять поля. Доступно коммерческому директору и собственнику',
    enumsManage: 'Нет права менять справочники. Доступно коммерческому директору и собственнику',
    rolesManage: 'Нет права менять права ролей. Доступно собственнику',
  },
} as const
